import axios from 'axios';
import ApiInstance from '@/middlewares/api-interceptore';
import { ROUTES } from './routes';

/**
 * GET /onboarding/status - Clinic Owner or Therapist. Returns { flow, steps, nextStep }.
 * steps/nextStep are null when there is no active onboarding flow for this user.
 * 403 (onboarding.errors.not_applicable) for any other role.
 */
const getOnboardingStatus = async () => {
  const { data } = await ApiInstance.get(ROUTES.STATUS);
  return data?.data ?? data;
};

/**
 * POST /onboarding/skip - Therapist only. Durably marks the therapist's own
 * ACTIVATION step as complete, the same as adding a first patient would.
 * Idempotent - repeat calls are a harmless no-op. Returns the same shape as
 * getOnboardingStatus, already reflecting the skip (nextStep: null).
 */
const skipOnboarding = async () => {
  const { data } = await ApiInstance.post(ROUTES.SKIP);
  return data?.data ?? data;
};

/**
 * PATCH /onboarding/profile - stamps the PROFILE onboarding step as complete.
 * Returns the full { message, data } envelope (not unwrapped) - callers need
 * `message` for the success toast, on top of the updated profile record.
 */
const updateOnboardingProfile = async payload => {
  const { data } = await ApiInstance.patch(ROUTES.PROFILE, payload);
  return data;
};

/**
 * PATCH /onboarding/clinic - stamps the CLINIC onboarding step as complete.
 * Returns the full { message, data } envelope - see updateOnboardingProfile.
 */
const updateOnboardingClinic = async payload => {
  const { data } = await ApiInstance.patch(ROUTES.CLINIC, payload);
  return data;
};

const presignClinicLogo = async ({ fileName, contentType }) => {
  const { data } = await ApiInstance.post(ROUTES.CLINIC_LOGO_PRESIGN, { fileName, contentType });
  return data?.data ?? data;
};

// Presigned S3 POST: must bypass ApiInstance - it targets the bucket origin, not
// API_BASE_URL, and must not carry the app's Authorization header (breaks the
// signature). Field order matters for S3's POST policy: every `fields` entry
// must be appended before the file part.
const uploadToPresignedPost = async ({ url, fields, file }) => {
  const formData = new FormData();
  Object.entries(fields || {}).forEach(([key, value]) => formData.append(key, value));
  formData.append('file', file);
  await axios.post(url, formData);
};

const confirmClinicLogo = async ({ fileId, fileName }) => {
  const { data } = await ApiInstance.put(ROUTES.CLINIC_LOGO_BY_ID(fileId), { fileName });
  return data?.data ?? data;
};

/** Full self-service clinic logo upload: presign -> upload straight to the bucket -> confirm. */
const uploadClinicLogo = async file => {
  const presign = await presignClinicLogo({ fileName: file.name, contentType: file.type });
  await uploadToPresignedPost({ url: presign.upload.url, fields: presign.upload.fields, file });
  return confirmClinicLogo({ fileId: presign.fileId, fileName: file.name });
};

/** DELETE /onboarding/clinic/logo - removes the clinic's current logo, if any. */
const deleteClinicLogo = async () => {
  const { data } = await ApiInstance.delete(ROUTES.CLINIC_LOGO);
  return data?.data ?? data;
};

export const onboardingApis = {
  getOnboardingStatus,
  skipOnboarding,
  updateOnboardingProfile,
  updateOnboardingClinic,
  uploadClinicLogo,
  deleteClinicLogo,
};
